import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled, { css } from 'styled-components';
import classNames from 'classnames';

// constants
import { colors, darken } from '../../style';
import { actionColor, actionLight } from '../../style/theme-helpers';

import { Div } from '../box';

export interface SquareTabProps {
  className?: string;
  href?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  size?: keyof typeof sizes;
  componentClass?: React.ElementType | string;
  to?: string | RouteComponentProps['location'];
  onClick?: React.MouseEventHandler<any>;
}

export const sizes = {
  lg: css`
    font-size: 1rem;
  `,
};

const TabComponent = ({
  className,
  disabled,
  active,
  componentClass: Component = 'button',
  to,
  ...rest
}: SquareTabProps) => (
  <Component
    type={Component === 'button' ? 'button' : undefined}
    activeClassName={to ? 'active' : undefined}
    to={to}
    className={classNames(className, { disabled, active })}
    disabled={disabled}
    role="tab"
    aria-selected={!!active}
    {...rest}
  />
);

const SquareTab = styled(TabComponent)`
  border: none;
  background: white;
  color: ${actionColor};
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 32px;
  transition: 0.2s ease-in-out;
  flex: 1 1 0;
  width: 0;

  &:not(:first-child) {
    border-left: 1px solid ${colors.gray300};

    &.active {
      border-left: 1px solid ${actionColor};
    }
  }

  ${props => sizes[props.size || 'lg']};

  &:not([disabled]),
  &:not(.disabled) {
    cursor: pointer;
  }

  &:hover:not(.disabled) {
    background: ${actionLight};
  }

  &:hover:active:not(.disabled),
  &:hover.active:not(.disabled) {
    color: white;
    background: ${props => darken(0.2, actionColor(props))};
  }
  &:active,
  &.active {
    color: white;
    background: ${actionColor};
  }

  &.disabled {
    color: ${colors.gray600};
    background: ${colors.gray100};
    cursor: not-allowed;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${colors.sky};
  }
`;

interface SquareTabGroupProps {
  'aria-label': string;
  children?: React.ReactNode;
}

export const SquareTabGroup = styled((props: SquareTabGroupProps) => {
  return <Div role="tablist" {...props} />;
})`
  border: 1px solid ${colors.gray300};
  display: flex;
  flex: 1;
  border-radius: 2px;
  overflow: hidden;
`;
export default SquareTab;
